import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Grid,
} from '@mui/material';
import { Send, Globe, Phone, MessageSquare, Mail } from 'lucide-react';
import Reveal from './Reveal';
import CountUp from './CountUp';
import { tokens } from '../theme';

const stats: {
  end: number;
  prefix?: string;
  suffix?: string;
  label: string;
}[] = [
  { end: 78, suffix: '%', label: 'Buy from first responder' },
  { end: 21, suffix: '×', label: 'More leads at 5 min vs 30' },
  { end: 73, suffix: '%', label: 'Leads never contacted' },
  { end: 30, prefix: '<', suffix: 's', label: 'AI response time' },
];

/* Line-icon brand glyphs (1.5px stroke) — lucide-react no longer ships
   brand icons, so these match the lucide stroke style exactly. */
const InstagramGlyph = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const FacebookGlyph = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
  </svg>
);

const WhatsAppGlyph = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 21l1.65-4.5A8 8 0 1 1 8 20a8 8 0 0 1-1.5-.35z" />
    <path d="M9 9c.3-.8.6-.8 1-.8.3 0 .6 0 .82.5l.6 1.4c.1.2 0 .4-.12.52l-.4.45c-.1.12-.1.3 0 .43a5 5 0 0 0 2.3 2.2c.2.1.32.02.43-.06l.46-.5c.13-.16.32-.2.5-.12l1.4.6c.2.1.3.3.3.5 0 .82-.7 1.5-1.5 1.5a7 7 0 0 1-6.3-6.2z" />
  </svg>
);

const platforms = [
  { name: 'Instagram', hover: '#E1306C', icon: <InstagramGlyph /> },
  { name: 'Facebook', hover: '#1877F2', icon: <FacebookGlyph /> },
  { name: 'WhatsApp', hover: '#25D366', icon: <WhatsAppGlyph /> },
  { name: 'Telegram', hover: '#26A5E4', icon: <Send size={18} strokeWidth={1.5} /> },
  { name: 'SMS', hover: tokens.sage, icon: <MessageSquare size={18} strokeWidth={1.5} /> },
  { name: 'Voice', hover: tokens.copper, icon: <Phone size={18} strokeWidth={1.5} /> },
  { name: 'Email', hover: tokens.copperDark, icon: <Mail size={18} strokeWidth={1.5} /> },
  { name: 'Web chat', hover: tokens.ink, icon: <Globe size={18} strokeWidth={1.5} /> },
];

export default function TrustIndicators() {
  return (
    <Box
      component="section"
      aria-label="Key statistics"
      sx={{
        py: 7,
        bgcolor: tokens.bandAlt,
        borderTop: `1px solid ${tokens.border}`,
        borderBottom: `1px solid ${tokens.border}`,
      }}
    >
      <Container maxWidth="lg">
        {/* stats — 4-up desktop, 2x2 at xs */}
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {stats.map((stat, i) => (
            <Grid key={stat.label} size={{ xs: 6, md: 3 }}>
              <Reveal delay={i * 70}>
                <Box
                  sx={{
                    textAlign: 'left',
                    borderLeft: {
                      xs: 'none',
                      md: i === 0 ? 'none' : '1px solid #D9D0BF',
                    },
                    pl: { xs: 0, md: i === 0 ? 0 : 3 },
                  }}
                >
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: tokens.fontDisplay,
                      fontWeight: 700,
                      fontSize: { xs: '1.9rem', sm: '2.25rem', md: '2.875rem' },
                      lineHeight: 1.05,
                      letterSpacing: '-0.01em',
                      color: 'text.primary',
                    }}
                  >
                    <CountUp
                      end={stat.end}
                      prefix={stat.prefix}
                      suffix={stat.suffix}
                    />
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      mt: 1.25,
                      fontSize: { xs: '0.75rem', md: '0.8125rem' },
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: tokens.copper,
                      lineHeight: 1.45,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Reveal>
            </Grid>
          ))}
        </Grid>

        {/* platforms — monochrome, brand color on hover */}
        <Reveal delay={280}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ mt: { xs: 5, md: 6 }, flexWrap: 'wrap', rowGap: 1.5 }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              }}
            >
              Works with
            </Typography>
            <Stack direction="row" spacing={1.25}>
              {platforms.map((p) => (
                <Box
                  key={p.name}
                  title={p.name}
                  aria-label={p.name}
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: `${tokens.radiusControl}px`,
                    border: `1px solid ${tokens.border}`,
                    bgcolor: tokens.bgPaper,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: tokens.textSecondary,
                    transition: `color ${tokens.motion.micro}ms ease, border-color ${tokens.motion.micro}ms ease`,
                    '&:hover': {
                      color: p.hover,
                      borderColor: p.hover,
                    },
                  }}
                >
                  {p.icon}
                </Box>
              ))}
            </Stack>
          </Stack>
        </Reveal>
      </Container>
    </Box>
  );
}
